import { Box, Step, StepLabel, Stepper, styled } from '@mui/material'
import { CarrierType, CarrierVisitDirection } from '@planning/app/api'
import { usePlanningStore } from '@planning/AppProvider'
import { ChangeTransportSubFormStore } from '@planning/components/organisms/ChangeTransportSubForm'
import { useMutualCarrierTypeCleanup } from '@planning/hooks/useMutualCarrierTypeCleanup'
import { useOrderNavigation } from '@planning/hooks/useOrderNavigation'
import { adminActionService } from '@planning/services'
import { useTranslate } from '@tolgee/react'
import { FormStore, useRegisterSubFormStore, validateCarrierTypes } from '@tom-ui/utils'
import { observer } from 'mobx-react-lite'
import { useEffect } from 'react'
import { ContainerVisit } from '../../stores/ContainerVisitItem'
import { CreateOrderPageViewStore } from '../../stores/CreateOrderPageViewStore'
import { DirectionalCargoDetailsType } from '../../V2/organisms/DirectionalCargoDetailsSubForm'
import { CargoDetailsSubFormStore } from '../../V2/stores/CargoDetailsSubFormStore'
import { OrderFormStoreType } from '../../V2/stores/OrderFormStore'
import { mapToContainerCardData } from './molecules/UnitContainerCard'
import { OrderCargoDetailsStep } from './steps/OrderCargoDetailsStep'
import { OrderReviewStep } from './steps/OrderReviewStep'
import { OrderTransportStep } from './steps/OrderTransportStep'
import UnitStep from './steps/UnitStep'

const StyledStep = styled(Step, {
  shouldForwardProp: prop => prop !== 'isActive' && prop !== 'isCompleted',
})<{ isActive: boolean; isCompleted: boolean }>(({ theme, isActive, isCompleted }) => ({
  borderTop: `4px solid ${isActive || isCompleted ? theme.palette.primary.main : '#E0E0E0'}`,
  paddingTop: 8,
}))

const StyledStepLabel = styled(StepLabel)(({ theme }) => ({
  cursor: 'pointer',
  userSelect: 'none',

  '& .MuiStepLabel-iconContainer': {
    display: 'none',
  },
  '& .MuiStepLabel-label': {
    fontWeight: 500,
    color: '#9E9E9E',
    transition: 'color 0.3s',
  },
  '& .MuiStepLabel-label.Mui-active': {
    color: theme.palette.primary.main,
    fontWeight: 600,
  },
}))

type Props = {
  createOrderPageViewStore: CreateOrderPageViewStore
  containerId?: number
  initialValues?: Partial<OrderFormStoreType>
  inboundCarrierType?: string
  outboundCarrierType?: string
  outboundCarrierVisitId?: number
}

export const StepperForm = observer((props: Props) => {
  const { t } = useTranslate()
  const {
    containerId,
    initialValues,
    createOrderPageViewStore,
    inboundCarrierType,
    outboundCarrierType,
    outboundCarrierVisitId,
  } = props
  const { formStore, stepperFormViewStore, orderReviewStore, isFromRestowPage } =
    createOrderPageViewStore
  const { activeStep } = stepperFormViewStore
  const { appViewStore } = usePlanningStore()

  const inboundCargoDetailsFormStore = useRegisterSubFormStore(
    formStore,
    'inboundCargoDetails',
    new FormStore<DirectionalCargoDetailsType>(),
  )

  const outboundCargoDetailsFormStore = useRegisterSubFormStore(
    formStore,
    'outboundCargoDetails',
    new FormStore<DirectionalCargoDetailsType>(),
  )

  const commonCargoDetailsFormStore = useRegisterSubFormStore(
    formStore,
    'cargoDetails',
    new FormStore<DirectionalCargoDetailsType>(),
  )

  const cargoDetailsFormStore = useRegisterSubFormStore(
    commonCargoDetailsFormStore,
    'cargoDetails',
    new CargoDetailsSubFormStore(initialValues?.cargoDetails?.cargoDetails, isFromRestowPage),
  )

  const inboundTransportStore = useRegisterSubFormStore(
    inboundCargoDetailsFormStore,
    'transport',
    new ChangeTransportSubFormStore(initialValues?.inboundCargoDetails?.transport),
  )
  if (inboundCarrierType) inboundTransportStore.carrierType.setValue(inboundCarrierType)
  inboundTransportStore.setContainerNumberAndDirection(
    createOrderPageViewStore.container?.number,
    CarrierVisitDirection.Inbound,
  )

  const outboundTransportStore = useRegisterSubFormStore(
    outboundCargoDetailsFormStore,
    'transport',
    new ChangeTransportSubFormStore(initialValues?.outboundCargoDetails?.transport),
  )
  if (outboundCarrierType) outboundTransportStore.carrierType.setValue(outboundCarrierType)
  outboundTransportStore.setContainerNumberAndDirection(
    createOrderPageViewStore.container?.number,
    CarrierVisitDirection.Outbound,
  )

  useMutualCarrierTypeCleanup(inboundTransportStore.carrierType, outboundTransportStore.carrierType)

  useEffect(() => {
    if (outboundCarrierVisitId && outboundCarrierType === CarrierType.Vessel) {
      outboundTransportStore.carrierVisitId.setValue(outboundCarrierVisitId)
      outboundTransportStore.loadVesselVisit(outboundCarrierVisitId)
    }

    if (outboundCarrierVisitId && outboundCarrierType === CarrierType.Train) {
      outboundTransportStore.carrierVisitId.setValue(outboundCarrierVisitId)
      outboundTransportStore.loadRailVisit(outboundCarrierVisitId)
    }
  }, [outboundCarrierVisitId, outboundCarrierType, outboundTransportStore])

  useEffect(() => {
    if (!createOrderPageViewStore.containerRemainOnBoardReference) return

    const remainOnBoardData = createOrderPageViewStore.containerRemainOnBoardReference

    cargoDetailsFormStore.isEmpty.setValue(remainOnBoardData.isEmpty ?? true)

    if (remainOnBoardData.grossWeight)
      cargoDetailsFormStore.grossWeight.setValue(remainOnBoardData.grossWeight)

    cargoDetailsFormStore.dangerousGoods.isDangerous.setValue(
      !!remainOnBoardData.imoClasses?.length,
    )

    cargoDetailsFormStore.dangerousGoods.imoClasses.setValue(remainOnBoardData.imoClasses ?? [])

    inboundTransportStore.bay.setValue(remainOnBoardData.origin?.bay?.toString() ?? '')
    inboundTransportStore.row.setValue(remainOnBoardData.origin?.row?.toString() ?? '')
    inboundTransportStore.tier.setValue(remainOnBoardData.origin?.tier?.toString() ?? '')
  }, [createOrderPageViewStore.containerRemainOnBoardReference])

  useEffect(() => {
    createOrderPageViewStore.registerStepValidators(1, [
      () =>
        validateCarrierTypes(inboundTransportStore.carrierType, outboundTransportStore.carrierType),

      () => inboundTransportStore.validate(),
      () => outboundTransportStore.validate(),
    ])

    createOrderPageViewStore.registerStepValidators(2, [
      () => cargoDetailsFormStore.validate(),
      () => cargoDetailsFormStore.dangerousGoods.validate(),
      () => commonCargoDetailsFormStore.validate(),
      () => inboundCargoDetailsFormStore.validate(),
      () => outboundCargoDetailsFormStore.validate(),
      () => formStore.validate(),
    ])
  }, [
    inboundTransportStore,
    outboundTransportStore,
    cargoDetailsFormStore,
    commonCargoDetailsFormStore,
    inboundCargoDetailsFormStore,
    outboundCargoDetailsFormStore,
    createOrderPageViewStore,
    formStore,
  ])

  const isAddedToTruckAppointment =
    inboundCarrierType === CarrierType.Truck || outboundCarrierType === CarrierType.Truck

  const { navigateJourneyOrderPage } = useOrderNavigation()

  const onEditOrder = async (containerVisit: ContainerVisit) => {
    const orderId = containerVisit[0]?.id ?? containerVisit[1]?.id

    navigateJourneyOrderPage({
      orderId,
    })
  }

  const onDeleteOrder = async (containerVisit: ContainerVisit) => {
    const inboundOrderId = containerVisit[0]?.id
    const outboundOrderId = containerVisit[1]?.id
    const hasServiceOrders =
      containerVisit[0]?.data.hasServiceOrders || containerVisit[1]?.data.hasServiceOrders
    const isConfirmed = await appViewStore.setOpenConfirmDialog(
      true,
      t(
        'journeyWillBeDeletedWarning',
        'The entire Journey and all associated orders will be deleted',
      ),
      t('confirmJourneyDelete', 'Confirm journey delete'),
      hasServiceOrders
        ? t(
            'anyAssociatedStrippingOrStuffingOrdersWillBeDeleted',
            'Any associated stripping/stuffing orders will be deleted',
          )
        : undefined,
    )

    if (!isConfirmed) return

    const orderIds = []

    if (inboundOrderId && inboundOrderId !== -1) orderIds.push(inboundOrderId)
    if (outboundOrderId && outboundOrderId !== -1) orderIds.push(outboundOrderId)

    try {
      await adminActionService.deleteByIds(orderIds)

      appViewStore.setShowAlert(
        'success',
        t('successfullyDeletedJourney', 'Successfully deleted journey'),
      )
    } catch (e) {
      appViewStore.setShowAlert('error', t('failedToDeleteJourney', 'Failed to delete journey'))
    }
  }

  const steps = [
    {
      label: t('unit', 'Unit'),
      component: (
        <UnitStep
          formStore={formStore}
          createOrderPageViewStore={createOrderPageViewStore}
          onEditOrder={isAddedToTruckAppointment ? undefined : onEditOrder}
          onDeleteOrder={onDeleteOrder}
        />
      ),
    },
    {
      label: t('transport', 'Transport'),
      component: (
        <OrderTransportStep
          formStore={formStore}
          inboundTransportStore={inboundTransportStore}
          outboundTransportStore={outboundTransportStore}
          disabled={createOrderPageViewStore.isFromRestowPage}
        />
      ),
    },
    {
      label: t('cargo', 'Cargo'),
      component: (
        <OrderCargoDetailsStep
          formStore={formStore}
          cargoDetailsFormStore={cargoDetailsFormStore}
          directionalCargoDetailsFormStore={commonCargoDetailsFormStore}
          containerId={containerId ?? 0}
          initialValues={initialValues}
          isFromRestowPage={createOrderPageViewStore.isFromRestowPage}
          holdsStore={createOrderPageViewStore.holdsStore}
        />
      ),
    },
    {
      label: t('review&Submit', 'Review & Submit'),
      component: (
        <OrderReviewStep
          containerId={containerId ?? 0}
          container={mapToContainerCardData(createOrderPageViewStore.container)}
          formStore={formStore}
          stepperFormViewStore={stepperFormViewStore}
          orderReviewStore={orderReviewStore}
          inboundTransportStore={inboundTransportStore}
          outboundTransportStore={outboundTransportStore}
          holdsStore={createOrderPageViewStore.holdsStore}
        />
      ),
    },
  ]

  stepperFormViewStore.setTotalSteps(steps.length)

  return (
    <Box>
      <Stepper activeStep={activeStep} alternativeLabel connector={null} nonLinear>
        {steps.map(({ label }, index) => (
          <StyledStep key={label} isActive={index === activeStep} isCompleted={index < activeStep}>
            <StyledStepLabel
              onClick={async () => {
                await createOrderPageViewStore.validateAndChangeStep(index)
              }}
            >
              {label}
            </StyledStepLabel>
          </StyledStep>
        ))}
      </Stepper>

      {steps.map(({ label, component }, index) => (
        <Box sx={{ display: activeStep === index ? 'block' : 'none' }} key={label} mt={2}>
          {component}
        </Box>
      ))}
    </Box>
  )
})
